import type { ComponentProps, ReactElement, ReactNode } from 'react';
import { useSidebarOpenState } from '@backstage/core-components';
import { ButtonIcon, Focusable, Tooltip, TooltipTrigger } from '@backstage/ui';
import type { ButtonIconProps } from '@backstage/ui';

/**
 * The name of a rail entry, on hover and on keyboard focus, while the menu
 * is compact; nothing at all while it is expanded, where the name is
 * written. The entry keeps its own accessible name: the tooltip only shows
 * it.
 *
 * `children` must forward its ref and spread unknown props onto a focusable
 * element (`Focusable`'s contract): a SidebarItem WITH `to` does — it hands
 * them to its NavLink — but not one without, whose MUI button drops them
 * (core-components 0.18.14, Items.esm.js): the buttons of the rail are
 * `RailButton` instead.
 */
export function RailTooltip(props: {
  label: string;
  children: ComponentProps<typeof Focusable>['children'];
}) {
  const { isOpen } = useSidebarOpenState();
  if (isOpen) {
    return props.children;
  }
  return (
    <TooltipTrigger>
      <Focusable>{props.children}</Focusable>
      <Tooltip placement="right">{props.label}</Tooltip>
    </TooltipTrigger>
  );
}

/**
 * A button of the compact rail: an icon, centred in a 72 × 48 px row like
 * the nav icons, its name as tooltip. The tooltip text defaults to the
 * accessible name.
 */
export function RailButton(
  props: Omit<ButtonIconProps, 'icon' | 'variant' | 'size' | 'className'> & {
    'aria-label': string;
    icon: ReactElement;
    tooltip?: ReactNode;
  },
) {
  const { icon, tooltip, ...buttonProps } = props;
  return (
    <div className="waadoo-rail-row">
      <TooltipTrigger>
        <ButtonIcon
          {...buttonProps}
          variant="tertiary"
          size="medium"
          icon={icon}
          className="waadoo-rail-button"
        />
        <Tooltip placement="right">{tooltip ?? props['aria-label']}</Tooltip>
      </TooltipTrigger>
    </div>
  );
}
